import type { WorkdaySyncRun, WorkdaySyncStatus } from '@ft/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { dtfmt, eur2 } from '../lib/format';
import { api } from '../lib/http';
import { useLoad } from './ui';

/**
 * Stand des Workday-Abgleichs auf der Invoicing-Seite: wann zuletzt, was dazukam, Button „Jetzt synchronisieren“.
 * Der Button legt eine Anforderung an; der stündliche Lauf (Claude über Chrome) erledigt sie und meldet das Ergebnis zurück.
 */
export function WorkdaySyncBox({ canEdit, onSynced }: { canEdit: boolean; onSynced: () => void }) {
  const { t } = useI18n();
  const { data, reload } = useLoad<WorkdaySyncStatus>('/api/workday/sync');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  // Während eine Anforderung offen ist, jede Minute nachsehen; wenn sie erledigt ist, Rechnungen neu laden
  const pendingId = data?.pending?.id;
  useEffect(() => {
    if (!pendingId) return;
    const h = setInterval(reload, 60_000);
    return () => clearInterval(h);
  }, [pendingId, reload]);
  const lastId = data?.last?.id;
  const [seen, setSeen] = useState<string | undefined>();
  useEffect(() => { if (lastId && seen && lastId !== seen) onSynced(); setSeen(lastId); }, [lastId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function request() {
    setBusy(true); setErr(null);
    try { await api('POST', '/api/workday/sync/request'); reload(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  if (!data) return null;
  const last = data.last;
  return (
    <div className="box wdsync" aria-labelledby="wdsync-h">
      <div className="wdsync-row">
        <div className="wdsync-main">
          <div className="lbl" id="wdsync-h">{t('sync.title')}</div>
          {last ? <LastRun run={last} /> : <div className="muted">{t('sync.never')}</div>}
          {last?.status === 'failed' && data.lastSuccess && <div className="muted small">{t('sync.lastSuccess', { at: dtfmt(data.lastSuccess.finishedAt!) })}</div>}
          {data.pending && <div className="wdsync-pending" role="status">{t('sync.pending', { by: data.pending.requestedBy ?? '–', at: dtfmt(data.pending.requestedAt!) })}</div>}
        </div>
        <span className="spacer" />
        {last && (last.newCount > 0 || last.unmatchedCount > 0 || data.recent.length > 1) && (
          <button className="btn ghost" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{open ? t('sync.hide') : t('sync.details')}</button>
        )}
        {canEdit && <button className="btn" onClick={request} disabled={busy || !!data.pending}>{data.pending ? t('sync.requested') : busy ? t('sync.requesting') : t('sync.now')}</button>}
      </div>
      {err && <p className="err small">{err}</p>}
      {open && last && (
        <div className="wdsync-detail">
          {last.newInvoices.length > 0 && (
            <>
              <div className="lbl">{t('sync.newList', { n: last.newInvoices.length })}</div>
              <table><tbody>{last.newInvoices.map((i) => <tr key={i.number}><td className="mono">{i.number}</td><td>{i.project}</td><td className="r num">{eur2(i.amountCents)}</td></tr>)}</tbody></table>
            </>
          )}
          {last.unmatched.length > 0 && (
            <>
              <div className="lbl">{t('sync.unmatchedList', { n: last.unmatched.length })}</div>
              <ul className="small">{last.unmatched.map((u) => <li key={u.number}><span className="mono">{u.number}</span> · {u.projectText}</li>)}</ul>
              {canEdit && <p className="small">{t('sync.unmatchedHint')} <Link to="/invoicing/workday">{t('wd.button')}</Link></p>}
            </>
          )}
          {data.recent.length > 1 && (
            <>
              <div className="lbl">{t('sync.history')}</div>
              <ul className="small wdsync-hist">{data.recent.map((r) => <li key={r.id}><LastRun run={r} compact /></li>)}</ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function LastRun({ run, compact }: { run: WorkdaySyncRun; compact?: boolean }) {
  const { t } = useI18n();
  const when = run.finishedAt ? dtfmt(run.finishedAt) : '–';
  const how = t(run.kind === 'auto' ? 'sync.kind.auto' : 'sync.kind.manual', { by: run.by ?? '–' });
  if (run.status === 'failed') {
    return <div className={compact ? '' : 'wdsync-fail'}><b>{when}</b> · {how} · {t('sync.failed')}{run.message ? `: ${run.message}` : ''}</div>;
  }
  const parts = [
    run.newCount ? <span key="n" className="wdsync-new">{t('sync.new', { n: run.newCount })}</span> : <span key="n">{t('sync.nothingNew')}</span>,
    run.unmatchedCount ? <span key="u" className="wdsync-warn">{t('sync.unmatched', { n: run.unmatchedCount })}</span> : null,
    run.differsCount ? <span key="d">{t('sync.differs', { n: run.differsCount })}</span> : null,
  ].filter(Boolean);
  return <div><b>{when}</b> · {how}{parts.map((p, i) => <span key={i}> · {p}</span>)}</div>;
}
